// src/pages/commercial/CommercialDashboardPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import CommercialFeed from '../../components/commercial/CommercialFeed';
import CommercialSmartWidget from '../../components/commercial/CommercialSmartWidget';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';

function CommercialDashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        dealsCount: 0,
        potentialAmount: 0,
        meetingsToday: 0,
        pendingQuotes: 0,
        salesWon: 0
    });
    const [loading, setLoading] = useState(true);

    // Sales Target (Example: 50k)
    const SALES_TARGET = 50000;

    useEffect(() => {
        async function fetchStats() {
            setLoading(true);
            try {
                // Fetch Deals
                const deals = await pb.collection('deals').getFullList({
                    sort: '-updated',
                    filter: `owner = "${user.id}"`
                });
                const potentialAmount = deals.filter(d => d.status !== 'won' && d.status !== 'lost').reduce((acc, deal) => acc + (deal.amount || 0), 0);
                const wonAmount = deals.filter(d => d.status === 'won').reduce((acc, deal) => acc + (deal.amount || 0), 0);

                // Fetch Meetings Today
                const todayCaledar = new Date().toISOString().split('T')[0];
                const meetings = await pb.collection('meetings').getList(1, 50, {
                    filter: `start >= "${todayCaledar} 00:00:00" && owner = "${user.id}"`,
                    sort: 'start'
                });

                // Fetch Draft/Sent Quotes
                const quotes = await pb.collection('quotes').getList(1, 1, {
                    filter: `(status = "sent" || status = "draft") && owner = "${user.id}"`,
                });

                setStats({
                    dealsCount: deals.length,
                    potentialAmount,
                    salesWon: wonAmount,
                    meetingsToday: meetings.items.filter(m => m.start.startsWith(todayCaledar)).length,
                    pendingQuotes: quotes.totalItems
                });

            } catch (err) {
                console.error("Error fetching commercial stats", err);
            } finally {
                setLoading(false);
            }
        }
        fetchStats();
    }, [user.id]);

    const progressPercent = Math.min(100, Math.round((stats.salesWon / SALES_TARGET) * 100));

    // Determine current motivational message based on stats
    const getMotivationalMessage = () => {
        if (stats.salesWon === 0) return "Décrochez votre première vente pour débloquer votre potentiel !";
        if (progressPercent < 20) return "Bon début ! Gardez le rythme pour atteindre vos objectifs.";
        if (progressPercent < 50) return "Vous êtes sur la bonne voie ! La moitié du chemin est faite.";
        return "Excellent travail ! Vous dominez le marché.";
    };

    return (
        <CommercialLayout title="Tableau de Bord">
            <div className="commercial-page-wrapper">
                <style>{`
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }
                
                .btn-action { 
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    padding: 20px; border-radius: 16px;
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    color: var(--admin-text);
                    cursor: pointer;
                    transition: all 0.2s;
                    height: 100%;
                    text-align: center;
                }
                .btn-action:hover {
                    box-shadow: 0 10px 30px -5px rgba(0,0,0,0.1);
                    transform: translateY(-4px);
                    border-color: var(--admin-accent);
                }
                .btn-action span.ico { font-size: 2.5rem; marginBottom: 10px; }
                .btn-action span.lbl { font-weight: 600; font-size: 1rem; }

                .kpi-card-modern {
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    border-radius: 16px;
                    padding: 24px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    position: relative;
                    overflow: hidden;
                }
                .kpi-card-modern::after {
                    content: ''; position: absolute; top: 0; right: 0; width: 100px; height: 100%;
                    background: linear-gradient(90deg, transparent, var(--admin-panel-2));
                    opacity: 0.5;
                }
                .kpi-val { font-size: 2.5rem; font-weight: 800; line-height: 1; margin-bottom: 5px; color: var(--admin-text); }
                .kpi-lbl { font-size: 0.9rem; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--admin-muted); opacity: 0.8; }
                .kpi-ico { position: absolute; top: 20px; right: 20px; font-size: 2rem; opacity: 0.2; }

            `}</style>

                {/* WELCOME HEADER */}
                <div style={{ marginBottom: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--admin-accent)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10 }}>
                        Command Center • Niveau 2
                    </div>
                    <h1 style={{ fontSize: '3rem', fontWeight: 800, margin: 0, lineHeight: 1.1 }}>
                        Prêt à conquérir le marché, <br />
                        <span style={{ color: 'var(--admin-accent)' }}>{user?.firstname || user?.name?.split(' ')[0] || 'Commercial'}</span> ?
                    </h1>
                    <p style={{ maxWidth: 600, margin: '15px auto 0', fontSize: '1.2rem', color: 'var(--admin-muted)' }}>
                        {getMotivationalMessage()}
                    </p>
                </div>

                {/* KPI GRID */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginBottom: 40 }}>
                    <div className="kpi-card-modern">
                        <div className="kpi-val">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(stats.salesWon)}</div>
                        <div className="kpi-lbl">Chiffre d'Affaires</div>
                        <div className="kpi-ico">💰</div>
                        <div style={{ marginTop: 15, width: '100%', height: 4, background: 'var(--admin-border)', borderRadius: 2 }}>
                            <div style={{ width: `${progressPercent}%`, height: '100%', background: 'var(--admin-accent)', borderRadius: 2 }}></div>
                        </div>
                        <div style={{ marginTop: 5, fontSize: '0.8rem', color: 'var(--admin-muted)' }}>Objectif: 50k€ ({progressPercent}%)</div>
                    </div>

                    <div className="kpi-card-modern">
                        <div className="kpi-val">{stats.dealsCount}</div>
                        <div className="kpi-lbl">Opportunités en cours</div>
                        <div className="kpi-ico">🔥</div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--admin-muted)', marginTop: 10 }}>
                            Potentiel total : <strong>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(stats.potentialAmount)}</strong>
                        </div>
                    </div>

                    <div className="kpi-card-modern">
                        <div className="kpi-val">{stats.meetingsToday}</div>
                        <div className="kpi-lbl">RDV Aujourd'hui</div>
                        <div className="kpi-ico">📅</div>
                        {stats.meetingsToday > 0 ? (
                            <div style={{ marginTop: 10, padding: '4px 10px', background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', borderRadius: 20, fontSize: '0.8rem', fontWeight: 'bold' }}>
                                Action requise
                            </div>
                        ) : (
                            <div style={{ marginTop: 10, fontSize: '0.8rem', opacity: 0.5 }}>Rien à signaler</div>
                        )}
                    </div>
                </div>

                {/* LAUNCHPAD GRID */}
                <h3 style={{ fontSize: '1.5rem', marginBottom: 20, fontWeight: 700 }}>Actions Rapides</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20, marginBottom: 40 }}>
                    <div className="btn-action" onClick={() => navigate('/commercial/launchpad')}>
                        <span className="ico">🛠️</span>
                        <span className="lbl">Mode Outils</span>
                        <span style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: 5 }}>Calculatrice, Notes...</span>
                    </div>

                    <div className="btn-action" onClick={() => navigate('/commercial/presentation')}>
                        <span className="ico">📱</span>
                        <span className="lbl">Mode Démo</span>
                        <span style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: 5 }}>Présentation, Offres</span>
                    </div>

                    <div className="btn-action" onClick={() => navigate('/commercial/deals')}>
                        <span className="ico">💼</span>
                        <span className="lbl">Nouveau Deal</span>
                        <span style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: 5 }}>Ajouter au pipeline</span>
                    </div>

                    <div className="btn-action" onClick={() => navigate('/commercial/clients')}>
                        <span className="ico">👤</span>
                        <span className="lbl">Nouveau Client</span>
                        <span style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: 5 }}>Créer une fiche</span>
                    </div>

                    <div className="btn-action" onClick={() => navigate('/commercial/devis')}>
                        <span className="ico">📝</span>
                        <span className="lbl">Créer un Devis</span>
                        <span style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: 5 }}>Proposition commerciale</span>
                    </div>
                </div>

                <div className="main-dashboard-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 40 }}>
                    {/* FEED */}
                    <div>
                        <h3 style={{ fontSize: '1.5rem', marginBottom: 20, fontWeight: 700 }}>Activité Récente</h3>
                        <CommercialFeed />
                    </div>

                    {/* WIDGETS */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <CommercialSmartWidget />
                        <div style={{ padding: 24, background: 'linear-gradient(135deg, #1e293b, #0f172a)', borderRadius: 16, color: 'white' }}>
                            <div style={{ fontSize: '2rem', marginBottom: 10 }}>💡</div>
                            <h4 style={{ margin: '0 0 10px 0' }}>Astuce Pro</h4>
                            <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.8, lineHeight: 1.5 }}>
                                Utilisez le <strong>Mode Tablette</strong> en rendez-vous pour une présentation fluide et impressionnante de vos offres.
                            </p>
                        </div>
                    </div>
                </div>

            </div>
        </CommercialLayout>
    );
}

export default CommercialDashboardPage;
